import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, RotateCcw, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { createAmbient } from "@/lib/ambient";

const SOUNDS = [
    { key: "none", label: "Silence" },
    { key: "rain", label: "Rain" },
    { key: "forest", label: "Forest" },
    { key: "lofi", label: "Lo-fi" },
];

export default function Focus() {
    const [duration, setDuration] = useState(25); // minutes
    const [remaining, setRemaining] = useState(25 * 60);
    const [running, setRunning] = useState(false);
    const [sound, setSound] = useState("none");
    const [volume, setVolume] = useState(50);
    const [muted, setMuted] = useState(false);
    const intervalRef = useRef(null);
    const ambientRef = useRef(null);

    useEffect(() => { if (!running) setRemaining(duration * 60); }, [duration, running]);

    useEffect(() => {
        if (running) {
            intervalRef.current = setInterval(() => {
                setRemaining(r => {
                    if (r <= 1) {
                        clearInterval(intervalRef.current);
                        setRunning(false);
                        logSession(duration, true);
                        toast.success("Focus session complete", { description: `${duration} minutes logged 🌱` });
                        return 0;
                    }
                    return r - 1;
                });
            }, 1000);
        }
        return () => clearInterval(intervalRef.current);
    }, [running]);

    useEffect(() => {
        // Stop any previous ambient
        if (ambientRef.current) { ambientRef.current.stop(); ambientRef.current = null; }
        if (sound === "none") return;
        const amb = createAmbient(sound);
        if (!amb) return;
        ambientRef.current = amb;
        amb.setVolume(muted ? 0 : volume / 100);
        return () => { amb.stop(); };
    }, [sound]);

    useEffect(() => {
        ambientRef.current?.setVolume(muted ? 0 : volume / 100);
    }, [volume, muted]);

    useEffect(() => () => { ambientRef.current?.stop(); }, []);

    const logSession = async (mins, completed) => {
        try { await api.post("/focus", { duration_minutes: mins, completed }); }
        catch (e) { console.warn("[focus] failed to log session", e); }
    };

    const start = () => setRunning(true);
    const pause = () => setRunning(false);
    const reset = () => { setRunning(false); setRemaining(duration * 60); };

    const total = duration * 60;
    const pct = ((total - remaining) / total) * 100;
    const mm = String(Math.floor(remaining / 60)).padStart(2, "0");
    const ss = String(remaining % 60).padStart(2, "0");

    const R = 140;
    const C = 2 * Math.PI * R;
    const offset = C - (pct / 100) * C;

    return (
        <div className="relative -m-6 md:-m-8 lg:-m-10 min-h-[calc(100vh-3.5rem)] lg:min-h-screen flex flex-col items-center justify-center p-6 overflow-hidden" data-testid="focus-page">
            {/* Ambient background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-background to-accent/10 dark:from-primary/40 dark:via-background dark:to-primary/20" />
            <div className="absolute inset-0 opacity-40" style={{
                background: "radial-gradient(circle at 30% 20%, hsl(var(--primary)/0.15), transparent 60%), radial-gradient(circle at 80% 80%, hsl(var(--accent)/0.15), transparent 60%)"
            }} />
            <div className="grain absolute inset-0" />

            <div className="relative z-10 text-center max-w-2xl w-full">
                <div className="mono-label mb-3">Focus mode</div>
                <h1 className="font-display text-3xl md:text-4xl font-medium tracking-tight mb-10">Deep work in progress.</h1>

                <div className="relative inline-block mb-10">
                    <svg width="320" height="320" viewBox="0 0 320 320" className="-rotate-90">
                        <circle cx="160" cy="160" r={R} stroke="hsl(var(--border))" strokeWidth="6" fill="none" />
                        <circle cx="160" cy="160" r={R} stroke="hsl(var(--primary))" strokeWidth="6" fill="none"
                            strokeLinecap="round" strokeDasharray={C} strokeDashoffset={offset}
                            style={{ transition: "stroke-dashoffset 1s linear" }} />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                        <div className="font-mono text-7xl md:text-8xl tracking-tighter tabular-nums" data-testid="timer-display">{mm}:{ss}</div>
                        <div className="mono-label mt-2">{duration} minute session</div>
                    </div>
                </div>

                <div className="flex justify-center gap-3 mb-8">
                    {!running ? (
                        <Button size="lg" onClick={start} className="gap-2 h-12 px-8" data-testid="timer-start"><Play className="w-4 h-4" /> Start</Button>
                    ) : (
                        <Button size="lg" onClick={pause} variant="secondary" className="gap-2 h-12 px-8" data-testid="timer-pause"><Pause className="w-4 h-4" /> Pause</Button>
                    )}
                    <Button size="lg" onClick={reset} variant="ghost" className="gap-2 h-12" data-testid="timer-reset"><RotateCcw className="w-4 h-4" /> Reset</Button>
                </div>

                <div className="glass rounded-2xl p-5 max-w-md mx-auto space-y-4">
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <span className="mono-label">Duration</span>
                            <span className="text-sm font-mono">{duration} min</span>
                        </div>
                        <Slider value={[duration]} onValueChange={([v]) => setDuration(v)} min={5} max={90} step={5} disabled={running} data-testid="duration-slider" />
                    </div>

                    <div>
                        <div className="mono-label mb-2">Ambient</div>
                        <div className="flex gap-2 flex-wrap">
                            {SOUNDS.map(s => (
                                <button key={s.key} onClick={() => setSound(s.key)} data-testid={`sound-${s.key}`}
                                    className={`text-xs px-3 py-1.5 rounded-full border transition ${sound === s.key ? "bg-primary text-primary-foreground border-primary" : "border-border/60 hover:bg-muted"}`}>
                                    {s.label}
                                </button>
                            ))}
                        </div>
                        {sound !== "none" && (
                            <div className="flex items-center gap-3 mt-3">
                                <button onClick={() => setMuted(m => !m)} className="p-1.5 rounded-md hover:bg-muted">
                                    {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                                </button>
                                <Slider value={[volume]} onValueChange={([v]) => setVolume(v)} min={0} max={100} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
